/**
 * Prerecorded TTS Service (Cloudflare R2 Audio & Mapping JSON Manager)
 * 
 * Supports 4 languages:
 * - Português (5k words): .../tts-words/portuguese/5k-audios-portuguese.json
 *   Audio path: .../tts-words/portuguese/audios/${text}_${id}.mp3
 * - Francês (27k words): .../tts-words/french/27k-audios-french.json
 *   Audio path: .../tts-words/french/audios/${text}-${id}.mp3
 * - Inglês (15k words): .../tts-words/english/15k-audios-english.json
 *   Audio path: .../tts-words/english/audio/${id}_${text}.mp3
 * - Italiano (20k words): .../tts-words/italian/20k-audios-italian.json
 *   Audio path: .../tts-words/italian/audio/${id}-${text}.mp3
 */

const R2_BASE = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/tts-words/';

export interface LanguageR2Config {
  langCode: string;
  jsonUrl: string;
  audioDir: string;
  filePattern: (id: number, text: string) => string;
}

const R2_LANG_CONFIGS: Record<string, LanguageR2Config> = {
  pt: {
    langCode: 'pt',
    jsonUrl: R2_BASE + 'portuguese/5k-audios-portuguese.json',
    audioDir: R2_BASE + 'portuguese/audios/',
    filePattern: (id, text) => `${text}_${id}.mp3`
  },
  fr: {
    langCode: 'fr',
    jsonUrl: R2_BASE + 'french/27k-audios-french.json',
    audioDir: R2_BASE + 'french/audios/',
    filePattern: (id, text) => `${text}-${id}.mp3`
  },
  en: {
    langCode: 'en',
    jsonUrl: R2_BASE + 'english/15k-audios-english.json',
    audioDir: R2_BASE + 'english/audio/',
    filePattern: (id, text) => `${id}_${text}.mp3`
  },
  it: {
    langCode: 'it',
    jsonUrl: R2_BASE + 'italian/20k-audios-italian.json',
    audioDir: R2_BASE + 'italian/audio/',
    filePattern: (id, text) => `${id}-${text}.mp3`
  },
  de: {
    langCode: 'de',
    jsonUrl: R2_BASE + 'german/5k-audios-german.json',
    audioDir: R2_BASE + 'german/audios/',
    filePattern: (id, text) => `id_${id}_${text.replace(/\s+/g, '_')}.mp3`
  },
  es: {
    langCode: 'es',
    jsonUrl: R2_BASE + 'spanish/5k-audios-spanish.json',
    audioDir: R2_BASE + 'spanish/audios/',
    filePattern: (id, text) => `id_${id}_${text.replace(/\s+/g, '_')}.mp3`
  },
  ru: {
    langCode: 'ru',
    jsonUrl: R2_BASE + 'russian/5k-audios-russian.json',
    audioDir: R2_BASE + 'russian/audios/',
    filePattern: (id, text) => `id_${id}_${text.replace(/\s+/g, '_')}.mp3`
  },
  hu: {
    langCode: 'hu',
    jsonUrl: R2_BASE + 'hungarian/6k-audios-hungarian.json',
    audioDir: R2_BASE + 'hungarian/audios/',
    filePattern: (id, text) => `id_${id}_${text.replace(/\s+/g, '_')}.mp3`
  }
};

export const isPrerecordedAudioEnabled = (): boolean => {
  return localStorage.getItem('libritui_use_prerecorded_audio') !== 'false';
};

export const normalizeR2Lang = (langInput: string): string | null => {
  if (!langInput) return null;
  const lower = langInput.toLowerCase().trim();
  if (lower.startsWith('pt') || lower.includes('portug')) return 'pt';
  if (lower.startsWith('fr') || lower.includes('french') || lower.includes('franc')) return 'fr';
  if (lower.startsWith('en') || lower.includes('engl') || lower.includes('ingl')) return 'en';
  if (lower.startsWith('it') || lower.includes('ital')) return 'it';
  if (lower.startsWith('de') || lower.includes('germ') || lower.includes('alem')) return 'de';
  if (lower.startsWith('es') || lower.includes('span') || lower.includes('espan')) return 'es';
  if (lower.startsWith('ru') || lower.includes('russ')) return 'ru';
  if (lower.startsWith('hu') || lower.includes('hung') || lower.includes('húng')) return 'hu';
  return null;
};

export interface R2WordItem {
  id: number;
  text: string;
}

export interface R2LangMaps {
  exactMap: Map<string, R2WordItem>;
  normMap: Map<string, R2WordItem>;
}

const jsonCache: Record<string, R2LangMaps> = {};
const loadingPromises: Record<string, Promise<R2LangMaps | null>> = {};

export const loadR2JsonMapping = async (normalizedLang: string): Promise<R2LangMaps | null> => {
  if (jsonCache[normalizedLang]) return jsonCache[normalizedLang];
  if (loadingPromises[normalizedLang]) return loadingPromises[normalizedLang];

  const config = R2_LANG_CONFIGS[normalizedLang];
  if (!config) return null;

  loadingPromises[normalizedLang] = (async () => {
    try {
      const res = await fetch(config.jsonUrl);
      if (!res.ok) return null;
      const data: R2WordItem[] = await res.json();
      const exactMap = new Map<string, R2WordItem>();
      const normMap = new Map<string, R2WordItem>();

      for (const item of data) {
        if (item && item.text !== undefined && item.text !== null) {
          const raw = item.text.toString().toLowerCase().trim();
          const norm = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

          if (!exactMap.has(raw)) exactMap.set(raw, item);
          if (!normMap.has(norm)) normMap.set(norm, item);
        }
      }
      const resultMaps: R2LangMaps = { exactMap, normMap };
      jsonCache[normalizedLang] = resultMaps;
      return resultMaps;
    } catch (err) {
      console.warn(`[prerecordedTtsService] Error loading R2 JSON for ${normalizedLang}:`, err);
      return null;
    } finally {
      delete loadingPromises[normalizedLang];
    }
  })();

  return loadingPromises[normalizedLang];
};

/**
 * Web Audio API Engine for Gapless, Instant Audio Playback
 */
class WebAudioR2Player {
  private ctx: AudioContext | null = null;
  private currentSource: AudioBufferSourceNode | null = null;
  private bufferCache: Map<string, AudioBuffer> = new Map();

  private getContext(): AudioContext {
    if (!this.ctx || this.ctx.state === 'closed') {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      this.ctx = new AudioCtx();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  async loadBuffer(url: string): Promise<AudioBuffer | null> {
    if (this.bufferCache.has(url)) return this.bufferCache.get(url)!;
    try {
      const ctx = this.getContext();
      const res = await fetch(url);
      if (!res.ok) return null;
      const arrayBuf = await res.arrayBuffer();
      const audioBuf = await ctx.decodeAudioData(arrayBuf);
      this.bufferCache.set(url, audioBuf);
      return audioBuf;
    } catch (e) {
      console.warn('[WebAudioR2Player] Preload/Decode error for:', url, e);
      return null;
    }
  }

  playBuffer(buffer: AudioBuffer): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const ctx = this.getContext();
        this.stop();

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        this.currentSource = source;

        source.onended = () => {
          this.currentSource = null;
          resolve(true);
        };

        source.start(0);
      } catch (err) {
        console.warn('[WebAudioR2Player] Play error:', err);
        this.stop();
        resolve(false);
      }
    });
  }

  stop() {
    if (this.currentSource) {
      try {
        this.currentSource.stop();
        this.currentSource.disconnect();
      } catch (e) {}
      this.currentSource = null;
    }
  }
}

export const webAudioR2Player = new WebAudioR2Player();

let currentHTMLAudio: HTMLAudioElement | null = null;

export const stopPrerecordedAudio = (): void => {
  webAudioR2Player.stop();
  if (currentHTMLAudio) {
    try {
      currentHTMLAudio.pause();
      currentHTMLAudio.onended = null;
      currentHTMLAudio.onerror = null;
    } catch (e) {}
    currentHTMLAudio = null;
  }
};

export const playR2Audio = async (url: string): Promise<boolean> => {
  stopPrerecordedAudio();

  // Try Web Audio API first for instant, gapless playback
  const buffer = await webAudioR2Player.loadBuffer(url);
  if (buffer) {
    const success = await webAudioR2Player.playBuffer(buffer);
    if (success) return true;
  }

  // Fallback to HTMLAudioElement
  return new Promise((resolve) => {
    try {
      const audio = new Audio(url);
      currentHTMLAudio = audio;

      audio.onended = () => {
        currentHTMLAudio = null;
        resolve(true);
      };

      audio.onerror = (e) => {
        console.warn(`[prerecordedTtsService] Audio error playing ${url}:`, e);
        currentHTMLAudio = null;
        resolve(false);
      };

      audio.play().catch((err) => {
        console.warn(`[prerecordedTtsService] audio.play() exception for ${url}:`, err);
        currentHTMLAudio = null;
        resolve(false);
      });
    } catch (err) {
      console.warn(`[prerecordedTtsService] HTMLAudioElement setup error:`, err);
      resolve(false);
    }
  });
};

export interface SpeakResult {
  success: boolean;
  source: 'r2' | 'fallback';
  foundInJson: boolean;
  word: string;
  url?: string;
}

export const lookupR2WordItem = async (word: string, langInput: string): Promise<{ item: R2WordItem; config: LanguageR2Config } | null> => {
  const normalizedLang = normalizeR2Lang(langInput);
  if (!normalizedLang) return null;

  const maps = await loadR2JsonMapping(normalizedLang);
  if (!maps) return null;

  const clean = word.trim().toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
  if (!clean) return null;

  const normKey = clean.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const item = maps.exactMap.get(clean) || maps.normMap.get(normKey) || null;
  if (!item) return null;

  const config = R2_LANG_CONFIGS[normalizedLang];
  return { item, config };
};

export const getR2AudioUrl = (item: R2WordItem, config: LanguageR2Config): string => {
  const filename = config.filePattern(item.id, item.text);
  return config.audioDir + encodeURI(filename);
};

export const speakSingleWordPrerecorded = async (
  word: string, 
  langInput: string
): Promise<SpeakResult> => {
  const lookup = await lookupR2WordItem(word, langInput);
  if (!lookup) {
    return { success: false, source: 'fallback', foundInJson: false, word };
  }

  const { item, config } = lookup;
  const primaryUrl = getR2AudioUrl(item, config);

  const played = await playR2Audio(primaryUrl);
  if (played) {
    return { success: true, source: 'r2', foundInJson: true, word: item.text, url: primaryUrl };
  }

  // Secondary candidate attempts if primary pattern fails
  const primaryFilename = config.filePattern(item.id, item.text);
  const candidateFilenames = [
    `id_${item.id}_${item.text.replace(/\s+/g, '_')}.mp3`,
    `id_${item.id}_${item.text}.mp3`,
    `${item.id}_${item.text}.mp3`,
    `${item.text}_${item.id}.mp3`,
    `${item.id}-${item.text}.mp3`,
    `${item.text}-${item.id}.mp3`
  ].filter(fn => fn !== primaryFilename);

  for (const altFn of candidateFilenames) {
    const altUrl = config.audioDir + encodeURI(altFn);
    const altPlayed = await playR2Audio(altUrl);
    if (altPlayed) {
      return { success: true, source: 'r2', foundInJson: true, word: item.text, url: altUrl };
    }
  }

  return { success: false, source: 'fallback', foundInJson: true, word: item.text, url: primaryUrl };
};
